import React, { useEffect } from "react";
import { useLocalStore, useStoreState } from "easy-peasy";
import { Box, Link, Paper, TableRow, TableCell } from "@mui/material";
import SearchInput from "components/admin_v2/ui/SearchInput";
import BatchSelect from "components/admin_v2/common/BatchSelect";
import Spinner from "components/admin_v2/ui/Spinner";
import PaginatedTable from "components/admin_v2/ui/PaginatedTable";
import TopPageLayout from "components/admin_v2/nav/TopPageLayout";
import SchoolConfigTabs from "components/admin_v2/school_config/SchoolConfigTabs";
import SchoolsFilter from "components/admin_v2/school_config/schools/SchoolsFilter";
import RouterLink from "components/admin_v2/nav/RouterLink";
import GreenCheckbox from "components/admin_v2/ui/GreenCheckbox";
import { OutlinedBtn } from "components/admin_v2/ui/Buttons";
import { schoolsStore } from "components/admin_v2/school_config/stores/schoolsStore";
import { updateWithRouter, camelizeLocationSearch } from "lib/queryString";
import { pageRoutes } from "services/api";
import { batchArchive, batchUnarchive } from "services/apiSchools";
import useSorting from "lib/useSorting";
import I18n from "utils/i18n.js";
import useDebounce from "lib/useDebounce";
import useDidMountEffect from "lib/useDidMountEffect";
import useCommonPageStyles from "components/admin_v2/common/useCommonPageStyles";

const Schools = (props) => {
  const isRestrictedAdmin = useStoreState((s) => s.app.isRestrictedAdmin);
  const cls = useCommonPageStyles();
  const store = useLocalStore(() => schoolsStore(camelizeLocationSearch(props)));
  const [state, actions] = store;
  const { district } = useStoreState((s) => s.app);
  const searchInput = useDebounce(state.query);
  const updateQueryString = updateWithRouter(props);
  const { sortBy, sortableColumn } = useSorting({ field: "created_at", order: "desc" });

  useEffect(() => {
    actions.setFromRouter(camelizeLocationSearch(props));
  }, [props.location.search]);

  useEffect(() => {
    actions.fetchSchools({ sortByField: sortBy.field, sortByOrder: sortBy.order });
  }, [searchInput, state.page, state.perPage, state.scope, sortBy, state.refresh]);

  useEffect(() => {
    updateQueryString({ page: 1, query: searchInput });
  }, [searchInput]);

  useDidMountEffect(() => {
    updateQueryString({ page: 1 });
    actions.toggleRefresh();
  }, [district]);

  let actionsList = [];
  if (!isRestrictedAdmin) {
    if (state.scope == "archived") {
      actionsList.push({ label: I18n.t("ui.btn.unarchive"), action: batchUnarchive });
    } else {
      actionsList.push({ label: I18n.t("ui.btn.archive"), action: batchArchive });
    }
  }

  const updatePage = (_e, newPage) => updateQueryString({ page: newPage + 1 });

  const updatePerPage = (e) => {
    updateQueryString({ page: 1, per_page: e.target.value });
  };

  const onSearch = (e) => {
    actions.setQuery(e.target.value);
  };

  const headers = [
    {
      field: "batchCheck",
      label: (
        <GreenCheckbox
          checked={state.allSelected}
          onChange={(e) => actions.updateAllSelected(e.target.checked)}
        />
      ),
      skipSorting: true
    },
    { field: "id", label: "ID" },
    { field: "name", label: "School" },
    { field: "slug", label: "Slug" },
    { field: "district_name", label: "District" },
    { field: "email", label: "Hotline Email" },
    { field: "created_at", label: "Created" },
    { field: "", label: "" }
  ].map(sortableColumn);

  const row = (school) => (
    <TableRow hover key={`school-${school.id}`}>
      <TableCell component="th" style={{ width: "5%" }}>
        <GreenCheckbox
          checked={state.isSelected(school.id)}
          onChange={(e) => actions.updateSelectedIds({ isAdd: e.target.checked, id: school.id })}
        />
      </TableCell>
      <TableCell component="th" style={{ width: "5%" }}>
        <RouterLink to={pageRoutes.school(school.id)} className={cls.tableLink}>
          {school.id}
        </RouterLink>
      </TableCell>
      <TableCell style={{ width: "15%" }}>{school.name}</TableCell>
      <TableCell style={{ width: "10%" }}>{school.slug}</TableCell>
      <TableCell style={{ width: "25%" }}>{school.district_name}</TableCell>
      <TableCell style={{ width: "15%" }}>{school.email}</TableCell>
      <TableCell>{school.created_at}</TableCell>
      <TableCell>
        <RouterLink to={pageRoutes.school(school.id)} className={cls.tableLink}>
          {I18n.t("ui.btn.view_edit")}
        </RouterLink>
        <Link
          onClick={() => actions.toggleArchive(school.id)}
          className={cls.tableLink}
          underline="hover"
          disabled={isRestrictedAdmin}
        >
          {school.discarded ? I18n.t("ui.btn.unarchive") : I18n.t("ui.btn.archive")}
        </Link>
      </TableCell>
    </TableRow>
  );

  return (
    <React.Fragment>
      <TopPageLayout title={I18n.t("school_config.title")} />
      <SchoolConfigTabs />
      <Box className={cls.btnsWrapper}>
        {!isRestrictedAdmin && (
          <RouterLink className={cls.actionBtn} to={pageRoutes.schoolNew()}>
            <OutlinedBtn>{I18n.t("school_config.new_school.btn")}</OutlinedBtn>
          </RouterLink>
        )}
      </Box>
      <Paper className={cls.paper}>
        <Box className={cls.filters}>
          <BatchSelect store={store} actionsList={actionsList} />
          <SchoolsFilter state={state} updateQueryString={updateQueryString} />
          <SearchInput cls value={state.query} onChange={onSearch} />
        </Box>
        {state.loading ? (
          <Spinner />
        ) : (
          <PaginatedTable
            headers={headers}
            count={state.total}
            perPage={state.perPage}
            page={state.page}
            onPageChange={updatePage}
            onChangePerPage={updatePerPage}
          >
            {state.schools.map(row)}
          </PaginatedTable>
        )}
      </Paper>
    </React.Fragment>
  );
};

export default Schools;
